import axios from '@/services/axiosInstance'

class WebsiteService {
  /**
   * Verifica la disponibilidad de un dominio
   * @param {string} domain - El dominio a verificar
   * @param {number|string} [websiteId] - Sitio que se está editando: su propio alias cuenta como disponible
   * @returns {Promise<{available: boolean, message: string}>}
   */
  async checkDomain(domain, websiteId = null) {
    try {
      const response = await axios.get('/websites/check-domain', {
        params: websiteId ? { domain, websiteId } : { domain }
      })
      return response.data
    } catch (error) {
      if (error.response?.status === 400) {
        throw new Error(error.response.data.message || 'El dominio es inválido')
      }
      throw new Error(error.response?.data?.message || 'Error al verificar el dominio')
    }
  }

  /**
   * Crea un nuevo sitio web
   * @param {Object} siteData - Datos del sitio web
   * @param {Object} siteData.template - Datos de la plantilla
   * @param {Object} siteData.config - Configuración del sitio
   * @returns {Promise<Object>}
   */
  async create(siteData) {
    try {
      const { template, config } = siteData
      const websiteData = {
        domain: config.alias,
        title: config.name,
        template: 'TemplatePreview',
        content: template
      }
      
      const response = await axios.post('/websites', websiteData)
      return response.data
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Error al crear el sitio web')
    }
  }

  /**
   * Obtiene un sitio web por su ID
   * @param {string|number} id - ID del sitio web
   * @returns {Promise<Object>}
   */
  async getById(id) {
    try {
      const response = await axios.get(`/websites/${id}`)
      return response.data
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Error al obtener el sitio web')
    }
  }

  /**
   * Obtiene todos los sitios web del usuario actual
   * @returns {Promise<Array>}
   */
  async getAllByUser() {
    try {
      const response = await axios.get('/websites')
      return response.data
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Error al obtener los sitios web')
    }
  }

  /**
   * Actualiza un sitio web
   * @param {string|number} id - ID del sitio web
   * @param {Object} updateData - Datos a actualizar
   * @returns {Promise<Object>}
   */
  async update(id, updateData) {
    try {
      const response = await axios.put(`/websites/${id}`, updateData)
      return response.data
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Error al actualizar el sitio web')
    }
  }

  /**
   * Elimina un sitio web
   * @param {string|number} id - ID del sitio web
   * @returns {Promise<void>}
   */
  async delete(id) {
    try {
      await axios.delete(`/websites/${id}`)
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Error al eliminar el sitio web')
    }
  }

  /**
   * Obtiene un sitio web por su dominio
   * @param {string} domain - Dominio del sitio web
   * @returns {Promise<Object>}
   */
  async getByDomain(domain) {
    try {
      const response = await axios.get(`/auth/domain/${domain}`)
      return response.data.website
    } catch (error) {
      // ✅ STEP 5: Preservar código de error para suscripción expirada
      if (error.response?.data?.code === 'WEBSITE_OWNER_SUBSCRIPTION_EXPIRED') {
        const err = new Error(error.response.data.message || 'Sitio temporalmente no disponible');
        err.code = 'WEBSITE_OWNER_SUBSCRIPTION_EXPIRED';
        err.redirectTo = error.response.data.redirectTo;
        throw err;
      }
      
      if (error.response?.status === 404) {
        throw new Error('Sitio web no encontrado')
      }
      throw new Error(error.response?.data?.message || 'Error al obtener el sitio web')
    }
  }
}

export default new WebsiteService() 